import React, { useEffect, useState } from 'react';
import ShowcaseReel from './ShowcaseReel';
import PhoneStack from './PhoneStack';

const SITE_URL = 'https://pawtimo.app';
// Pawtimo's own pinks: the app icon, its blush, and a deep rose for text on pink
const ROSE = '#D4657E';
const BLUSH = '#FCE9EE';
const DEEP = '#8E2F48';

const useIsMobile = (bp = 768) => {
  const [m, setM] = useState(typeof window !== 'undefined' ? window.innerWidth < bp : false);
  useEffect(() => {
    const on = () => setM(window.innerWidth < bp);
    on(); window.addEventListener('resize', on);
    return () => window.removeEventListener('resize', on);
  }, [bp]);
  return m;
};

const Pill = ({ children, solid }) => (
  <span style={{
    display: 'inline-block', padding: '7px 14px', borderRadius: 999, fontSize: '12px', fontWeight: 600,
    letterSpacing: '0.04em', background: solid ? ROSE : 'rgba(212,101,126,0.12)', color: solid ? '#fff' : ROSE,
    border: solid ? `1px solid ${ROSE}` : '1px solid rgba(212,101,126,0.3)'
  }}>{children}</span>
);

const Clock = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true" style={{ verticalAlign: '-4px', marginRight: '7px' }}>
    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" />
    <path d="M12 7.5V12l3 2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const MetaItem = ({ label, value }) => (
  <div style={{ minWidth: 0 }}>
    <div style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.12em', color: 'var(--text-secondary)', marginBottom: '7px' }}>{label}</div>
    <div style={{ fontSize: '13.5px', fontWeight: 600, color: 'var(--text-primary)' }}>{value}</div>
  </div>
);

const Hero = () => (
  <div>
    <div style={{
      background: `radial-gradient(120% 90% at 50% 0%, #fff 0%, ${BLUSH} 70%)`, borderRadius: '28px',
      padding: 'clamp(36px, 7vw, 64px) 24px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '14px',
      border: '1px solid rgba(212,101,126,0.18)', marginBottom: 'clamp(28px, 4vw, 40px)'
    }}>
      <img src="/pawtimo-icon.png" alt="Pawtimo app icon" style={{
        width: 'clamp(92px, 14vw, 128px)', height: 'auto', borderRadius: '28%', boxShadow: '0 18px 40px rgba(212,101,126,0.35)'
      }} />
      <div style={{ fontSize: 'clamp(1rem, 2.2vw, 1.2rem)', fontWeight: 600, color: DEEP }}>Happy pets, calm family</div>
    </div>
    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '14px' }}>
      <Pill>Mobile app</Pill>
      <Pill solid><Clock />App Store</Pill>
    </div>
    <h1 style={{ margin: 0, fontSize: 'clamp(2rem, 5vw, 3rem)', fontWeight: 700, letterSpacing: '-0.03em', color: 'var(--text-primary)' }}>Pawtimo</h1>
    <p style={{ margin: '16px 0 0', maxWidth: '60ch', fontSize: '1.05rem', lineHeight: 1.7, color: 'var(--text-primary)' }}>
      A family app for pets. One tap logs a meal, everyone at home sees it straight away, and a reminder comes before the next one.
      Nobody feeds the cat twice, and nobody forgets the dog.
    </p>
    <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', margin: '24px 0 32px' }}>
      <a href={SITE_URL} target="_blank" rel="noopener noreferrer" style={{
        padding: '12px 20px', borderRadius: '14px', background: ROSE, color: '#fff', fontWeight: 600, fontSize: '14px', textDecoration: 'none'
      }}>Visit pawtimo.app →</a>
    </div>
    <div style={{
      // flex, not grid: responsive.css forces every inline "fr" grid to one column on phones
      display: 'flex', justifyContent: 'space-between', gap: 'clamp(8px, 3vw, 30px)', paddingTop: '24px', borderTop: '1px solid var(--border-color)'
    }}>
      <MetaItem label="Role" value="Founder" />
      <MetaItem label="Platform" value="iPhone" />
      <MetaItem label="For" value="Families with pets" />
      <MetaItem label="Status" value={<span><Clock />App Store</span>} />
    </div>
  </div>
);

/* The real site, running inside a soft pink browser frame. */
const LiveSite = () => {
  const mobile = useIsMobile();
  return (
    <div>
      <div style={{ borderRadius: '22px', overflow: 'hidden', border: '1px solid rgba(212,101,126,0.3)', boxShadow: '0 20px 50px rgba(212,101,126,0.18)', background: '#fff' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '7px', padding: '11px 14px', background: BLUSH, borderBottom: '1px solid rgba(212,101,126,0.2)' }}>
          {['#F4A3B5', '#F7C3CF', '#FADCE3'].map((c) => <span key={c} style={{ width: 11, height: 11, borderRadius: '50%', background: c }} />)}
          <span style={{ flex: 1, textAlign: 'center', fontSize: '12px', color: DEEP, fontWeight: 600 }}>pawtimo.app</span>
          <a href={SITE_URL} target="_blank" rel="noopener noreferrer" style={{ fontSize: '12px', color: DEEP, fontWeight: 600 }}>Open ↗</a>
        </div>
        <iframe src={SITE_URL} title="pawtimo.app, live" loading="lazy"
          style={{ display: 'block', width: '100%', height: mobile ? '560px' : '680px', border: 0, background: '#fff' }} />
      </div>
      <p style={{ marginTop: '12px', fontSize: '13px', color: 'var(--text-secondary)' }}>
        The real site, live. You can scroll it right here.
      </p>
    </div>
  );
};

const SITE_PAGES = [
  { file: 'home', label: 'Home', path: '/' },
  { file: 'support', label: 'Support', path: '/support' }
];

/* App Store screenshots, 2026-10-13. */
const SCREENS = [
  ['1.jpg', 'Happy pets, calm family'], ['2.jpg', 'One tap: fed'], ['3.jpg', 'Log a past meal'], ['4.jpg', 'The timer resets'],
  ['5.jpg', 'Dogs too'], ['6.jpg', 'Cats and dogs, one home'], ['7.jpg', 'Meal reminders'], ['8.jpg', 'Feeding history']
];

const Screens = () => (
  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px' }}>
    {SCREENS.map(([f, label]) => (
      <figure key={f} style={{ margin: 0, flex: '1 1 150px', maxWidth: 'calc(50% - 8px)', minWidth: '130px', textAlign: 'center' }}>
        <img src={`/portfolio-screens/pawtimo/${f}`} alt={label} loading="lazy" style={{
          width: '100%', display: 'block', borderRadius: '20px', boxShadow: '0 10px 26px rgba(212,101,126,0.22)'
        }} />
        <figcaption style={{ marginTop: '8px', fontSize: '12.5px', color: 'var(--text-secondary)' }}>{label}</figcaption>
      </figure>
    ))}
  </div>
);

const PinkEnd = () => (
  <div style={{
    borderRadius: '28px', padding: 'clamp(40px, 7vw, 64px) 24px', textAlign: 'center',
    background: `linear-gradient(135deg, ${ROSE} 0%, #E8899E 100%)`, color: '#fff', boxShadow: '0 22px 50px rgba(212,101,126,0.35)'
  }}>
    <img src="/pawtimo-icon.png" alt="" style={{ width: 64, borderRadius: '28%', boxShadow: '0 10px 24px rgba(0,0,0,0.15)' }} />
    <div style={{ fontSize: 'clamp(1.5rem, 4vw, 2rem)', fontWeight: 700, marginTop: '14px' }}>Pawtimo</div>
    <div style={{ fontSize: '15px', opacity: 0.92, marginTop: '6px' }}>Pet care, simplified</div>
    <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', flexWrap: 'wrap', marginTop: '22px' }}>
      <a href={SITE_URL} target="_blank" rel="noopener noreferrer" style={{
        padding: '12px 20px', borderRadius: '14px', background: '#fff', color: DEEP, fontWeight: 700, fontSize: '14px', textDecoration: 'none'
      }}>Visit pawtimo.app</a>
      <a href="/" style={{
        padding: '12px 20px', borderRadius: '14px', background: 'rgba(255,255,255,0.18)', color: '#fff', fontWeight: 600,
        fontSize: '14px', textDecoration: 'none', border: '1px solid rgba(255,255,255,0.5)'
      }}>See more work ↗</a>
    </div>
  </div>
);

export const pawtimoContent = {
  breadcrumb: { text: 'Back to Home', url: '/' },
  sections: [
    { id: 'intro-card', blocks: [{ type: 'custom', render: () => <ShowcaseReel slug="pawtimo" card="https://pawtimo.app" alt="Pawtimo" /> }] },
    { id: 'hero', spacing: 'clamp(48px, 7vw, 80px)', blocks: [{ type: 'custom', render: () => <Hero /> }] },
    { id: 'showcase-motion', blocks: [{ type: 'custom', render: () => <ShowcaseReel slug="pawtimo" video alt="Pawtimo" /> }] },
    { id: 'showcase-live', blocks: [{ type: 'custom', render: () => <ShowcaseReel slug="pawtimo" frames={[1, 2]} alt="Pawtimo" /> }] },
    { id: 'live', title: 'The website, live', blocks: [{ type: 'custom', render: () => <LiveSite /> }] },
    { id: 'showcase-site-pages', blocks: [{ type: 'custom', render: () => <ShowcaseReel slug="pawtimo" frames={[3, 4]} alt="Pawtimo" /> }] },
    { id: 'site-pages', title: 'The site on a phone', blocks: [{ type: 'custom', render: () => <PhoneStack pages={SITE_PAGES} baseUrl={SITE_URL} imgBase="/portfolio-screens/pawtimo/site/" /> }] },
    { id: 'showcase-screenshots-mobile', blocks: [{ type: 'custom', render: () => <ShowcaseReel slug="pawtimo" frames={[5, 6]} alt="Pawtimo" /> }] },
    { id: 'screenshots-mobile', title: 'Inside the app', blocks: [{ type: 'custom', render: () => <Screens /> }] },
    { id: 'showcase-end', blocks: [{ type: 'custom', render: () => <ShowcaseReel slug="pawtimo" frames={[7, 8]} alt="Pawtimo" /> }] },
    { id: 'end', blocks: [{ type: 'custom', render: () => <PinkEnd /> }] }
  ]
};
